import { observer } from 'mobx-react-lite';
import React from 'react';

import { playSfx, Sfx } from 'cfx/apps/mpMenu/utils/sfx';
import { useServiceOptional } from 'cfx/base/servicesContainer';
import { IServersConnectService } from 'cfx/common/services/servers/serversConnect.service';
import { timeout } from 'cfx/utils/async';
import { SingleEventListenerDisposer } from 'cfx/utils/singleEventEmitter';

import s from './CustomServerCards.module.scss';

// Random number that feels enough to wait for fail (mirrors ServerConnectButton)
const CONNECTION_TIMEOUT_TIME = 2500;

interface ServerDef {
  id: string;
  title: string;
  address: string;
  video: string;
  music: string;
}

const SERVERS: ServerDef[] = [
  {
    id: 'PLF',
    title: 'PLF PVP',
    address: 'pvp.proleague.com.br',
    video: 'https://files.proleague.com.br/files/default/new-loading.mp4?updateAt=1775745842394.9932',
    music: 'https://files.proleague.com.br/files/default/music.mp3?updateAt=1775746215000',
  },
  {
    id: 'NEXT',
    title: 'NEXT PVP',
    address: 'pvp.nextpvp.com.br',
    video: 'https://files.proleague.com.br/files/default/LOADSCREEN-NEXT.mp4?updateAt=1776536888147.8506',
    music: 'https://files.proleague.com.br/files/default/intro-next.ogg?updateAt=1776448544601.436',
  },
];

export const CustomServerCards = observer(function CustomServerCards() {
  const ServersConnectService = useServiceOptional(IServersConnectService);
  const stopConnectionListnerRef = React.useRef<SingleEventListenerDisposer | null>(null);

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [volume, setVolume] = React.useState(0);

  const selected = SERVERS[selectedIndex];

  // Swap video + music when the selected server changes
  React.useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (video) {
      video.src = selected.video;
      video.load();
      video.play().catch(() => {});
    }

    if (audio) {
      audio.src = selected.music;
      audio.load();
      audio.volume = volume / 100;
      audio.play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  // Keep audio volume in sync
  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  const toggleMusic = () => {
    setVolume((v) => (v > 0 ? 0 : 10));
  };

  const disabled = !ServersConnectService?.canConnect;

  const handleConnect = () => {
    if (!ServersConnectService) {
      return;
    }

    if (__CFXUI_USE_SOUNDS__) {
      playSfx(Sfx.Connect);
    }

    if (stopConnectionListnerRef.current) {
      stopConnectionListnerRef.current();
      stopConnectionListnerRef.current = null;
    }

    stopConnectionListnerRef.current = ServersConnectService.connectFailed.addListener(() => {
      // listener kept for parity; modal surfaces the failure UI
    });
    timeout(CONNECTION_TIMEOUT_TIME).finally(() => {
      if (stopConnectionListnerRef.current === null) {
        return;
      }

      stopConnectionListnerRef.current();
      stopConnectionListnerRef.current = null;
    });

    ServersConnectService.connectTo(selected.address);
  };

  return (
    <div className={s.root}>
      <video ref={videoRef} className={s.video} autoPlay muted loop playsInline />

      <div className={`${s.gradient} ${s.gradientBottom}`} />
      <div className={`${s.gradient} ${s.gradientSide}`} />

      <div className={s.panel}>
        <div className={s.panelLabel}>SELECIONE O SERVIDOR</div>

        <div className={s.serverTabs}>
          {SERVERS.map((server, index) => (
            <button
              key={server.id}
              type="button"
              className={index === selectedIndex ? `${s.serverTab} ${s.serverTabActive}` : s.serverTab}
              onClick={() => setSelectedIndex(index)}
            >
              {server.title}
            </button>
          ))}
        </div>

        <button
          type="button"
          className={s.connectButton}
          disabled={disabled}
          onClick={handleConnect}
        >
          CONECTAR EM {selected.title}
        </button>
      </div>

      <div className={s.volume}>
        <button type="button" className={s.volumeIcon} onClick={toggleMusic}>
          {volume > 0
            ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path fill="currentColor" d="M5 6.5v11H1v-11zm2 11.71 8 4.5V1.29l-8 4.5zM21.157 6.343l-.707-.707-1.415 1.414.708.707a6 6 0 0 1 0 8.485l-.707.707 1.414 1.415.707-.707a8 8 0 0 0 0-11.314" />
                <path fill="currentColor" d="m18.682 7.404-1.414 1.414.707.707a3.5 3.5 0 0 1 0 4.95l-.707.707 1.414 1.414.707-.707a5.5 5.5 0 0 0 0-7.778z" />
                <path fill="currentColor" d="M17.621 14.121a3 3 0 0 0 0-4.243l-.707-.707-1.414 1.415.707.707a1 1 0 0 1 0 1.414l-.707.707 1.414 1.414z" />
              </svg>
              )
            : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path fill="currentColor" d="M5 6.5v11H1v-11zm2 11.71 8 4.5V1.29l-8 4.5zm13-7.624-2.12-2.122-1.415 1.415 2.121 2.12-2.121 2.122 1.414 1.414L20 13.415l2.122 2.12 1.414-1.414L21.415 12l2.12-2.121-1.413-1.415z" />
              </svg>
              )}
        </button>

        <div className={s.volumeInput} style={{ ['--volume-width' as any]: `${volume}%` }}>
          <input
            type="range"
            name="volume"
            min="0"
            max="100"
            step="1"
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
          />
          <div className={s.volumeFill} />
        </div>
      </div>

      <audio ref={audioRef} loop />
    </div>
  );
});
